import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import TsKayitShell from '../components/TsKayitShell';
import {
  fetchSifreLinkDurumu,
  fetchSifreTeknikServisler,
  sifreBelirle,
  sifreKodDogrula,
  sifreKodGonder,
  sifreServisSec,
} from '../services/sifreSifirlamaService';
import './TsKayitPublicPage.css';

/*
 * Şifremi unuttum (/sifre/:token) — yönetici linki açtığında çalışır.
 * 1) Servis seç  2) Telefon (maskeli numara gösterilir)  3) Kullanıcı adı + SMS kodu  4) Yeni şifre
 * Şifre kuralları backend/constants/tsKayitBasvuru.js ile aynı.
 */

const SIFRE_KURALLARI = [
  { id: 'uzunluk', label: '8-32 karakter', test: (v) => v.length >= 8 && v.length <= 32 },
  { id: 'buyuk', label: 'En az 1 büyük harf (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { id: 'kucuk', label: 'En az 1 küçük harf (a-z)', test: (v) => /[a-z]/.test(v) },
  { id: 'rakam', label: 'En az 1 rakam (0-9)', test: (v) => /\d/.test(v) },
  { id: 'turkce', label: 'Türkçe karakter ve boşluk yok', test: (v) => v.length > 0 && /^[\x21-\x7E]+$/.test(v) },
];

const ADIMLAR = ['Servisinizi seçin', 'Telefon numaranızı doğrulayın', 'SMS kodunu girin', 'Yeni şifre belirleyin'];

const TELEFON_UYARI = 'Numaranızı başında 0 olmadan 10 hane olarak girin (örn. 5XX XXX XX XX).';

function normalizeAra(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/i̇/g, 'i')
    .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .trim();
}

function formatSure(saniye) {
  const s = Math.max(0, saniye);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function KuralListesi({ kurallar, dolu }) {
  return (
    <ul className="ts-kayit-kurallar">
      {kurallar.map((k) => (
        <li key={k.id} className={k.passed ? 'passed' : (dolu ? 'failed' : '')}>
          <i className={`fas ${k.passed ? 'fa-check-circle' : (dolu ? 'fa-times-circle' : 'fa-circle')}`} aria-hidden="true" />
          <span>{k.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Saniye sayacı: bitis (ms) verilir, kalan saniyeyi döner */
function useKalanSaniye(bitis) {
  const [simdi, setSimdi] = useState(Date.now());
  useEffect(() => {
    if (!bitis) return undefined;
    const id = setInterval(() => setSimdi(Date.now()), 1000);
    return () => clearInterval(id);
  }, [bitis]);
  if (!bitis) return 0;
  return Math.max(0, Math.ceil((bitis - simdi) / 1000));
}

export default function SifreSifirlamaPublicPage() {
  const { token } = useParams();
  const [yukleniyor, setYukleniyor] = useState(true);
  const [linkHata, setLinkHata] = useState('');
  const [adim, setAdim] = useState(1);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');
  const [bekliyor, setBekliyor] = useState(false);

  const [servisler, setServisler] = useState([]);
  const [filtre, setFiltre] = useState('');
  const [seciliTs, setSeciliTs] = useState(null);
  const [maskeliTelefon, setMaskeliTelefon] = useState('');

  const [telefon, setTelefon] = useState('');
  const [telefonUyari, setTelefonUyari] = useState('');

  const [kodBitis, setKodBitis] = useState(null);
  const [tekrarBitis, setTekrarBitis] = useState(null);
  const [kullaniciAdi, setKullaniciAdi] = useState('');
  const [kod, setKod] = useState('');

  const [anahtar, setAnahtar] = useState('');
  const [sifre, setSifre] = useState('');
  const [sifreTekrar, setSifreTekrar] = useState('');
  const [sifreGoster, setSifreGoster] = useState(false);
  const [tamamlandi, setTamamlandi] = useState(false);

  const kodKalan = useKalanSaniye(kodBitis);
  const tekrarKalan = useKalanSaniye(tekrarBitis);

  const yukle = useCallback(async () => {
    if (!token) {
      setLinkHata('Bu bağlantı aktif değil.');
      setYukleniyor(false);
      return;
    }
    setYukleniyor(true);
    setLinkHata('');
    try {
      await fetchSifreLinkDurumu(token);
      const res = await fetchSifreTeknikServisler(token);
      setServisler(res?.veri?.items || []);
    } catch (err) {
      setLinkHata(err?.message || 'Bu bağlantı aktif değil.');
    } finally {
      setYukleniyor(false);
    }
  }, [token]);

  useEffect(() => { yukle(); }, [yukle]);

  const filtreli = useMemo(() => {
    const q = normalizeAra(filtre);
    if (!q) return servisler;
    return servisler.filter((ts) => normalizeAra(`${ts.ad} ${ts.ilAdi || ''} ${ts.ilceAdi || ''}`).includes(q));
  }, [servisler, filtre]);

  const hataGoster = (err, fallback) => {
    if (err?.status === 403) {
      setLinkHata(err.message || 'Bu bağlantı aktif değil.');
      return;
    }
    setHata(err?.message || fallback);
  };

  const servisDevam = async () => {
    if (!seciliTs) return;
    setHata('');
    setBekliyor(true);
    try {
      const res = await sifreServisSec(token, seciliTs.id);
      setMaskeliTelefon(res?.veri?.maskeliTelefon || '');
      setTelefon('');
      setTelefonUyari('');
      setAdim(2);
    } catch (err) {
      hataGoster(err, 'Servis bilgisi alınamadı');
    } finally {
      setBekliyor(false);
    }
  };

  const telefonDegisti = (deger) => {
    const rakamlar = String(deger || '').replace(/\D/g, '');
    if (rakamlar.startsWith('0')) {
      setTelefonUyari(TELEFON_UYARI);
      setTelefon(rakamlar.slice(0, 10));
      return;
    }
    if (rakamlar.length > 10) {
      setTelefonUyari(TELEFON_UYARI);
      setTelefon(rakamlar.slice(0, 10));
      return;
    }
    setTelefonUyari('');
    setTelefon(rakamlar);
  };

  const telefonGecerli = /^5\d{9}$/.test(telefon);

  const kodGonder = async () => {
    if (!telefonGecerli || !seciliTs) return;
    setHata('');
    setBilgi('');
    setBekliyor(true);
    try {
      const res = await sifreKodGonder(token, seciliTs.id, telefon);
      const v = res?.veri || {};
      const simdi = Date.now();
      setKodBitis(simdi + (v.gecerlilikSuresiSaniye || 120) * 1000);
      setTekrarBitis(simdi + (v.tekrarGonderSaniye || 60) * 1000);
      setBilgi(res?.mesaj || '');
      setKod('');
      setAdim(3);
    } catch (err) {
      hataGoster(err, 'Kod gönderilemedi');
    } finally {
      setBekliyor(false);
    }
  };

  const kodDogrula = async () => {
    setHata('');
    setBekliyor(true);
    try {
      const res = await sifreKodDogrula(token, {
        techServiceId: seciliTs?.id,
        kullaniciAdi: kullaniciAdi.trim(),
        kod: kod.trim(),
      });
      setAnahtar(res?.veri?.sifirlamaAnahtari || '');
      setSifre('');
      setSifreTekrar('');
      setAdim(4);
    } catch (err) {
      hataGoster(err, 'Doğrulama yapılamadı');
    } finally {
      setBekliyor(false);
    }
  };

  const sifreKurallar = SIFRE_KURALLARI.map((k) => ({ ...k, passed: k.test(sifre) }));
  const sifreOk = sifreKurallar.every((k) => k.passed);
  const uyusuyor = sifreTekrar.length > 0 && sifre === sifreTekrar;

  const sifreKaydet = async () => {
    if (!sifreOk || !uyusuyor) return;
    setHata('');
    setBekliyor(true);
    try {
      await sifreBelirle(token, { sifirlamaAnahtari: anahtar, yeniSifre: sifre, yeniSifreTekrar: sifreTekrar });
      setTamamlandi(true);
    } catch (err) {
      hataGoster(err, 'Şifre değiştirilemedi');
    } finally {
      setBekliyor(false);
    }
  };

  const bastanBasla = () => {
    setAdim(1);
    setHata('');
    setBilgi('');
    setKod('');
    setKullaniciAdi('');
    setAnahtar('');
    setKodBitis(null);
    setTekrarBitis(null);
  };

  const baslik = 'Şifremi Unuttum';

  if (yukleniyor) {
    return (
      <TsKayitShell title={baslik}>
        <div className="ts-kayit-loading">Yükleniyor...</div>
      </TsKayitShell>
    );
  }

  if (linkHata) {
    return (
      <TsKayitShell title={baslik}>
        <div className="ts-kayit-error">{linkHata}</div>
      </TsKayitShell>
    );
  }

  if (tamamlandi) {
    return (
      <TsKayitShell title={baslik}>
        <div className="ts-kayit-durum-kart onaylandi">
          <i className="fas fa-check-circle" />
          <p className="ts-kayit-durum-aciklama">
            Şifreniz değiştirildi. Yeni şifrenizle giriş yapabilirsiniz. Açık oturumlarınız güvenlik için kapatıldı.
          </p>
        </div>
        <div className="ts-kayit-actions">
          <a className="ts-kayit-btn-primary" href="/login" style={{ textAlign: 'center', textDecoration: 'none' }}>
            Giriş sayfasına git
          </a>
        </div>
      </TsKayitShell>
    );
  }

  return (
    <TsKayitShell title={baslik}>
      <div className="ts-kayit-adim-bilgi">
        <span>Adım {adim} / 4</span>
        <strong>{ADIMLAR[adim - 1]}</strong>
      </div>
      <div className="ts-kayit-steps">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className={`ts-kayit-step-dot${adim === n ? ' active' : ''}${adim > n ? ' done' : ''}`} />
        ))}
      </div>

      {adim === 1 && (
        <>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-ts-filtre">Teknik servisiniz</label>
            <input
              id="sifre-ts-filtre"
              type="search"
              value={filtre}
              onChange={(e) => setFiltre(e.target.value)}
              placeholder="Servis adı veya il yazın"
              autoComplete="off"
            />
            <div className="ts-kayit-ts-list ts-kayit-ts-list-tam" role="listbox" aria-label="Teknik servisler">
              {filtreli.length === 0 && <div className="ts-kayit-ts-bos">Sonuç bulunamadı.</div>}
              {filtreli.map((ts) => (
                <button
                  key={ts.id}
                  type="button"
                  role="option"
                  aria-selected={seciliTs?.id === ts.id}
                  className={`ts-kayit-ts-item${seciliTs?.id === ts.id ? ' selected' : ''}`}
                  onClick={() => setSeciliTs(ts)}
                >
                  <strong>{ts.ad}</strong>
                  <small>{[ts.ilAdi, ts.ilceAdi].filter(Boolean).join(' / ')}</small>
                </button>
              ))}
            </div>
            {seciliTs && (
              <div className="ts-kayit-selected-ts">
                Seçili servis: <strong>{seciliTs.ad}</strong>
              </div>
            )}
          </div>
          {hata && <div className="ts-kayit-hint err ts-kayit-form-hata">{hata}</div>}
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-primary" disabled={!seciliTs || bekliyor} onClick={servisDevam}>
              {bekliyor ? 'Yükleniyor...' : 'Devam'}
            </button>
          </div>
        </>
      )}

      {adim === 2 && (
        <>
          <div className="ts-kayit-selected-ts ts-kayit-ust-ozet">
            Servis: <strong>{seciliTs?.ad}</strong>
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-telefon">Kayıtlı cep telefonunuz</label>
            <div className="ts-kayit-hint" style={{ marginBottom: 8 }}>
              Sistemdeki numaranız <strong style={{ letterSpacing: 1 }}>{maskeliTelefon}</strong> ile bitiyor.
              Numaranızı başında 0 olmadan girin.
            </div>
            <input
              id="sifre-telefon"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="5XXXXXXXXX"
              value={telefon}
              onChange={(e) => telefonDegisti(e.target.value)}
            />
            {telefonUyari && <div className="ts-kayit-hint err">{telefonUyari}</div>}
            {!telefonUyari && telefon.length === 10 && !telefonGecerli && (
              <div className="ts-kayit-hint err">Lütfen 5 ile başlayan cep telefonu numaranızı girin.</div>
            )}
          </div>
          {hata && <div className="ts-kayit-hint err ts-kayit-form-hata">{hata}</div>}
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-secondary" onClick={() => { setHata(''); setAdim(1); }} disabled={bekliyor}>
              Geri
            </button>
            <button type="button" className="ts-kayit-btn-primary" disabled={!telefonGecerli || bekliyor} onClick={kodGonder}>
              {bekliyor ? 'Gönderiliyor...' : 'SMS kodu gönder'}
            </button>
          </div>
        </>
      )}

      {adim === 3 && (
        <>
          <div className="ts-kayit-selected-ts ts-kayit-ust-ozet">
            Servis: <strong>{seciliTs?.ad}</strong>
          </div>
          {bilgi && <div className="ts-kayit-hint ok" style={{ marginBottom: 12 }}>{bilgi}</div>}
          <div className="ts-kayit-hint" style={{ marginBottom: 12 }}>
            {kodKalan > 0
              ? <>Kodun kalan süresi: <strong>{formatSure(kodKalan)}</strong></>
              : <strong>Kodun süresi doldu. Yeni kod isteyin.</strong>}
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-kadi">Kullanıcı adı (SMS ile gelen)</label>
            <input
              id="sifre-kadi"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              value={kullaniciAdi}
              onChange={(e) => setKullaniciAdi(e.target.value)}
              maxLength={50}
            />
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-kod">Doğrulama kodu</label>
            <input
              id="sifre-kod"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6 haneli kod"
              value={kod}
              onChange={(e) => setKod(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </div>
          {hata && <div className="ts-kayit-hint err ts-kayit-form-hata">{hata}</div>}
          <div className="ts-kayit-actions">
            <button
              type="button"
              className="ts-kayit-btn-secondary"
              disabled={tekrarKalan > 0 || bekliyor}
              onClick={kodGonder}
            >
              {tekrarKalan > 0 ? `Yeni kod (${tekrarKalan} sn)` : 'Yeni kod gönder'}
            </button>
            <button
              type="button"
              className="ts-kayit-btn-primary"
              disabled={bekliyor || kod.length !== 6 || !kullaniciAdi.trim() || kodKalan === 0}
              onClick={kodDogrula}
            >
              {bekliyor ? 'Kontrol ediliyor...' : 'Doğrula'}
            </button>
          </div>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button type="button" className="ts-kayit-link-btn" onClick={() => { setHata(''); setAdim(2); }}>
              Numarayı yanlış mı girdiniz?
            </button>
          </div>
        </>
      )}

      {adim === 4 && (
        <>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-yeni">Yeni şifre</label>
            <input
              id="sifre-yeni"
              type={sifreGoster ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
              maxLength={32}
            />
            <KuralListesi kurallar={sifreKurallar} dolu={sifre.length > 0} />
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="sifre-yeni-tekrar">Yeni şifre tekrar</label>
            <input
              id="sifre-yeni-tekrar"
              type={sifreGoster ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              value={sifreTekrar}
              onChange={(e) => setSifreTekrar(e.target.value)}
              maxLength={32}
            />
            {sifreTekrar.length > 0 && !uyusuyor && <div className="ts-kayit-hint err">Şifreler eşleşmiyor</div>}
            {uyusuyor && <div className="ts-kayit-hint ok">Şifreler eşleşiyor</div>}
          </div>
          <label className="ts-kayit-checkbox">
            <input type="checkbox" checked={sifreGoster} onChange={(e) => setSifreGoster(e.target.checked)} />
            Şifreyi göster
          </label>
          {hata && (
            <div className="ts-kayit-hint err ts-kayit-form-hata">
              {hata}{' '}
              <button type="button" className="ts-kayit-link-btn" onClick={bastanBasla}>Baştan başla</button>
            </div>
          )}
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-primary" disabled={!sifreOk || !uyusuyor || bekliyor} onClick={sifreKaydet}>
              {bekliyor ? 'Kaydediliyor...' : 'Şifreyi değiştir'}
            </button>
          </div>
        </>
      )}
    </TsKayitShell>
  );
}
